import { useTranslation } from '@/hooks/useTranslation';
import { SurveyPublicSummaryDto } from '@/services/feedbackApi';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SurveyLandingSection.css';

interface SurveyLandingSectionProps {
  summary: SurveyPublicSummaryDto;
}

const SurveyLandingSection: React.FC<SurveyLandingSectionProps> = ({
  summary,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleStartSurvey = () => {
    navigate(`/survey/${summary.surveyId}/take`);
  };

  const handleViewMixer = () => {
    navigate(`/mixer/${summary.surveyId}`);
  };

  // Show "View Mixer" button if survey has a mixer question configured
  // The mixer question existence indicates we should try to fetch mixer data
  // The isVotingEnabled flag is checked later in VotingInterface to show/hide vote buttons
  const hasMixerQuestion =
    summary.mixerQuestionId !== null && summary.mixerQuestionId !== undefined;

  return (
    <main className='min-h-screen flex justify-center items-start p-8 bg-white'>
      <div className='survey-landing-container w-full rounded-lg px-10 pt-8 pb-7 mt-8'>
        <div className='survey-landing-inner-card bg-white rounded-lg p-10 mb-5'>
          <h1 className='survey-landing-title text-2xl font-bold mb-6'>
            {summary.title}
          </h1>

          <p className='text-gray-700 text-base mb-6 leading-relaxed'>
            {summary.description}
          </p>

          <div className='flex items-center gap-1 text-gray-700 text-sm'>
            <div className='flex items-center gap-2'>
              <i
                className='survey-landing-icon pi pi-clock'
                aria-hidden='true'
              />
              <span>
                {t('surveyLanding.timeToComplete')}:{' '}
                <span className='font-semibold'>
                  {summary.estimatedMinutes} {t('surveyLanding.minutes')}
                </span>
              </span>
            </div>
            <div className='flex items-center gap-2 ml-8'>
              <i
                className='survey-landing-icon pi pi-question-circle'
                aria-hidden='true'
              />
              <span>
                {t('surveyLanding.numberOfQuestions')}:{' '}
                <span className='font-semibold'>{summary.questionCount}</span>
              </span>
            </div>
          </div>
        </div>

        <div className='flex gap-3 flex-wrap pb-6'>
          <button
            onClick={handleStartSurvey}
            className='survey-landing-btn survey-landing-btn-primary'
            aria-label={t('surveyLanding.startSurvey')}
          >
            <i className='pi pi-check-circle' aria-hidden='true' />
            <span>{t('surveyLanding.startSurvey')}</span>
          </button>

          {hasMixerQuestion && (
            <button
              onClick={handleViewMixer}
              className='survey-landing-btn survey-landing-btn-secondary'
              aria-label={t('surveyLanding.viewMixerResponses')}
            >
              <i className='pi pi-comments' aria-hidden='true' />
              <span>{t('surveyLanding.viewMixerResponses')}</span>
            </button>
          )}
        </div>
      </div>
    </main>
  );
};

export default SurveyLandingSection;
